import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Icon } from "../../../components/Component";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";
import LicenceWorkflowHelp from "../../../components/features/licence/WorkflowHelp";
import ProfilePhotoBlock from "../../../components/features/practitioner/ProfilePhotoBlock";
import http from "../../../utils/http-common";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import { API_BASE_URL, getPublicUrl } from "../../../utils/Utils";
import {
  CHECKLIST_LABELS,
  formatVerifierLine,
  registrationNumberLabel,
  registryForCountry,
} from "../../../lib/licenceRegistry";
import swal from "sweetalert";
import { meCan } from "../../../utils/adminPermissions";

const REASON_LABELS = {
  credentials_missing: "Licence information missing or invalid",
  complaints: "Patient complaints",
  rules_violation: "Platform rules not respected",
  competition: "Competition / conflict of interest",
  other: "Other",
};

const BLOCKING_LABELS = CHECKLIST_LABELS;

const PANEL_WIDTH = "min(380px, 34vw)";
const PANEL_NARROW_BP = 992;

const SORT_KEYS = {
  name: "name",
  country: "country",
  requestedAt: "requestedAt",
  status: "status",
};

function assetUrl(relativeOrAbsolute) {
  const u = String(relativeOrAbsolute || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = String(API_BASE_URL || "").replace(/\/$/, "");
  const pathPart = u.startsWith("/") ? u : `/${u}`;
  return `${base}${pathPart}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function statusRank(row) {
  if (row.invisible) return -1;
  if (row.notListable) return 0;
  if (row.awaitingAdminVerify) return 1;
  if (row.ready) return 2;
  return 3;
}

function visibilityLabel(row) {
  if (row.invisible) return { text: "Invisible", hint: "Clinic blocks listing", className: "badge bg-danger" };
  if (row.notListable) {
    const miss = (row.missingListFields || [])
      .map((m) => (m === "specialty" ? "specialty" : "price"))
      .join(" / ");
    return {
      text: "Approved — offline",
      hint: miss ? `Missing: ${miss}` : "Incomplete profile",
      className: "badge bg-warning text-dark",
    };
  }
  if (row.ready) return { text: "Ready to publish", hint: "", className: "badge bg-success" };
  if (row.awaitingAdminVerify) {
    if (row.isReverification) {
      return { text: "Re-verify", hint: "Diploma/licence changed", className: "badge bg-info text-dark" };
    }
    return { text: "Licence", hint: "Awaiting verification", className: "badge bg-info text-dark" };
  }
  if (row.clinicBlocked) {
    return { text: "Not listed", hint: "Clinic pending", className: "badge bg-warning text-dark" };
  }
  if (row.licenceBlocked) {
    return { text: "Not listed", hint: "Licence blocks", className: "badge bg-warning text-dark" };
  }
  return { text: "Blocked", hint: "", className: "badge bg-warning text-dark" };
}

function licenceCell(row) {
  if (row.credentialsVerified) return { ok: true, label: "Verified" };
  if (row.isReverification) return { ok: false, label: "Re-verify" };
  if (row.awaitingAdminVerify) return { ok: false, label: "Verify" };
  return { ok: false, label: "Missing" };
}

const QUEUE_FILTERS = [
  { id: "all", label: "All" },
  { id: "licence", label: "Licence" },
  { id: "clinic", label: "Clinic pending" },
  { id: "notListable", label: "Approved offline" },
  { id: "ready", label: "Ready" },
  { id: "invisible", label: "Invisibles" },
];

function compareRows(a, b, sortKey, sortDir) {
  const dir = sortDir === "desc" ? -1 : 1;
  let cmp = 0;
  if (sortKey === "name") {
    cmp = String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
  } else if (sortKey === "country") {
    cmp = String(a.practiceCountry || "").localeCompare(String(b.practiceCountry || ""), undefined, {
      sensitivity: "base",
    });
  } else if (sortKey === "requestedAt") {
    const ta = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
    const tb = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
    cmp = ta - tb;
  } else if (sortKey === "status") {
    cmp = statusRank(a) - statusRank(b);
    if (cmp === 0) {
      const ta = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
      const tb = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
      cmp = ta - tb;
    }
  }
  return cmp * dir;
}

function clinicDecisionLabel(decision) {
  const d = String(decision || "pending").toLowerCase();
  if (d === "approved") return "Approved";
  if (d === "rejected") return "Rejected";
  return "Pending";
}

function BlockingDots({ blocking }) {
  const items = Array.isArray(blocking) ? blocking : [];
  const short = {
    credentialsVerified: "Verified",
    licensePresent: "Number",
    practiceCountry: "Country",
    accountNotBanned: "Account",
    clinicApproved: "Clinic",
  };
  return (
    <span className="d-inline-flex flex-wrap gap-1 small">
      {items.map((item) => (
        <span
          key={item.id}
          className={`badge ${item.ok ? "bg-success" : "bg-danger"}`}
          title={BLOCKING_LABELS[item.id] || item.label}
        >
          {item.ok ? "✓" : "✗"} {short[item.id] || item.id}
        </span>
      ))}
    </span>
  );
}

function ChecklistBlock({ title, items }) {
  const visible = (items || []).filter((item) => !item.disabled);
  if (!visible.length) return null;
  return (
    <div className="mb-3">
      <div className="text-soft small fw-bold mb-1">{title}</div>
      <ul className="list-unstyled mb-0 small">
        {visible.map((item) => (
          <li key={item.id} className="d-flex align-items-start gap-2 mb-1">
            <span aria-hidden="true">{item.ok ? "✅" : "❌"}</span>
            <span className={item.ok ? "" : "text-danger"}>
              {BLOCKING_LABELS[item.id] || item.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * PO queue: pending platform-listing practitioners — review one by one.
 */
const PractitionerApprovals = () => {
  const history = useHistory();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    hideTest: true,
    hiddenTestCount: 0,
    readyCount: 0,
    blockedCount: 0,
    awaitingVerifyCount: 0,
    licenceBlockedCount: 0,
    clinicBlockedCount: 0,
    invisibleCount: 0,
    notListableCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [hideTest, setHideTest] = useState(true);
  const [search, setSearch] = useState("");
  const [queueFilter, setQueueFilter] = useState("all");
  /** Default: status bucket (invisible → awaiting → ready → blocked) then oldest request. */
  const [sortKey, setSortKey] = useState(SORT_KEYS.status);
  const [sortDir, setSortDir] = useState("asc");
  const [activeId, setActiveId] = useState(null);
  const [activeClinicId, setActiveClinicId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [credsFlash, setCredsFlash] = useState(null);
  const [profileFlash, setProfileFlash] = useState(null);
  const [savingDecision, setSavingDecision] = useState(false);
  const [remindingListing, setRemindingListing] = useState(false);
  const [savingClinicDecision, setSavingClinicDecision] = useState(false);
  const [pickedReasons, setPickedReasons] = useState([]);
  const [note, setNote] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteChecks, setDeleteChecks] = useState({});
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1400
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isNarrowPanel = viewportWidth < PANEL_NARROW_BP;
  const panelOpen = Boolean(activeId);
  const queueMarginRight = panelOpen && !isNarrowPanel ? PANEL_WIDTH : undefined;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get("/api/v1/admin/practitioner-approvals", {
        params: { hideTest: hideTest ? "1" : "0", q: search || undefined },
        suppressInlineErrorSwal: true,
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setMeta(
        res.data?.meta || {
          hideTest,
          hiddenTestCount: 0,
          readyCount: 0,
          blockedCount: 0,
          awaitingVerifyCount: 0,
          licenceBlockedCount: 0,
          clinicBlockedCount: 0,
          invisibleCount: 0,
          notListableCount: 0,
        }
      );
      return data;
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not load pending practitioners"), "error");
      setRows([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [hideTest, search]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const id = new URLSearchParams(location.search || "").get("id");
    if (!id) return;
    setActiveId(String(id));
  }, [location.search]);

  const sortedRows = useMemo(() => {
    let list = [...rows];
    if (queueFilter === "licence") {
      list = list.filter((r) => r.licenceBlocked || r.awaitingAdminVerify);
    } else if (queueFilter === "clinic") {
      list = list.filter((r) => r.clinicBlocked && !r.invisible);
    } else if (queueFilter === "ready") {
      list = list.filter((r) => r.ready);
    } else if (queueFilter === "notListable") {
      list = list.filter((r) => r.notListable);
    } else if (queueFilter === "invisible") {
      list = list.filter((r) => r.invisible);
    }
    list.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return list;
  }, [rows, sortKey, sortDir, queueFilter]);

  const activeIndex = useMemo(
    () => sortedRows.findIndex((r) => String(r.id) === String(activeId)),
    [sortedRows, activeId]
  );

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const sortHint = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const loadDetail = useCallback(async (practitionerId, clinicIdHint) => {
    if (!practitionerId) {
      setDetail(null);
      setProfilePhotoUrl("");
      return;
    }
    setDetailLoading(true);
    try {
      const res = await http.get(
        `/api/v1/admin/practitioner-approvals/${practitionerId}/checklist`,
        { suppressInlineErrorSwal: true }
      );
      const payload = res.data?.data || res.data;
      setDetail(payload);
      const clinicId = payload?.profile?.clinicId || clinicIdHint || null;
      setActiveClinicId(clinicId);
      setPickedReasons(Array.isArray(payload?.rejectionReasons) ? payload.rejectionReasons : []);
      setNote(payload?.rejectionNote || "");

      let photo = "";
      if (clinicId) {
        try {
          const prRes = await http.get(`/api/v1/partners/medical/clinics/${clinicId}/practitioners`, {
            suppressInlineErrorSwal: true,
          });
          const list = Array.isArray(prRes.data?.data) ? prRes.data.data : [];
          const hit = list.find((p) => String(p._id) === String(practitionerId));
          photo = hit?.photoUrl ? String(hit.photoUrl) : "";
        } catch {
          photo = "";
        }
      }
      setProfilePhotoUrl(photo);
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not load checklist"), "error");
      setDetail(null);
      setProfilePhotoUrl("");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeId) {
      const row = rows.find((r) => String(r.id) === String(activeId));
      loadDetail(activeId, row?.clinicId || activeClinicId);
    } else setDetail(null);
  }, [activeId, loadDetail]);

  useEffect(() => {
    setRejectOpen(false);
    setCredsFlash(null);
    setProfileFlash(null);
    setDeleteOpen(false);
    setDeleteImpact(null);
    setDeleteChecks({});
  }, [activeId]);

  const openRow = (rowOrId) => {
    const row =
      rowOrId && typeof rowOrId === "object"
        ? rowOrId
        : sortedRows.find((r) => String(r.id) === String(rowOrId)) ||
          rows.find((r) => String(r.id) === String(rowOrId));
    const id = row?.id != null ? row.id : rowOrId;
    setAutoAdvance(false);
    setActiveId(String(id));
    setActiveClinicId(row?.clinicId || null);
  };

  const closeDrawer = () => {
    setActiveId(null);
    setDetail(null);
    setRejectOpen(false);
    setCredsFlash(null);
    setProfileFlash(null);
    setDeleteOpen(false);
    setDeleteImpact(null);
    setDeleteChecks({});
  };

  const goNextAfterDecision = async (decidedId) => {
    const prevIdx = sortedRows.findIndex((r) => String(r.id) === String(decidedId));
    const nextList = await loadList();
    if (nextList.length === 0) {
      closeDrawer();
      return;
    }
    const ordered = [...nextList].sort((a, b) => compareRows(a, b, sortKey, sortDir));
    const next = ordered[Math.min(Math.max(prevIdx, 0), ordered.length - 1)] || ordered[0];
    setAutoAdvance(true);
    setActiveId(String(next.id));
  };

  const saveProfile = async (payload, flashMsg) => {
    const profile = detail?.profile;
    const clinicId = profile?.clinicId || activeClinicId;
    if (!clinicId || !profile?.id) {
      swal("Error", "Missing clinic for this practitioner. Refresh the list or contact support.", "error");
      return;
    }
    setSavingProfile(true);
    setProfileFlash(null);
    try {
      await http.put(
        `/api/v1/partners/medical/clinics/${clinicId}/practitioners/${profile.id}`,
        payload,
        { suppressInlineErrorSwal: true }
      );
      setProfileFlash(flashMsg || "Saved");
      await loadDetail(profile.id);
      await loadList();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not save"), "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleCredentials = async (checked) => {
    const profile = detail?.profile;
    const clinicId = profile?.clinicId || activeClinicId;
    if (!clinicId || !profile?.id) {
      swal("Error", "Missing clinic for this practitioner. Refresh the list or contact support.", "error");
      return;
    }
    setSavingCreds(true);
    setCredsFlash(null);
    try {
      await http.put(
        `/api/v1/partners/medical/clinics/${clinicId}/practitioners/${profile.id}`,
        { credentialsVerified: checked },
        { suppressInlineErrorSwal: true }
      );
      setCredsFlash(
        checked
          ? "Licence marked as verified"
          : "Verification removed — listing set to pending if it was approved"
      );
      await loadDetail(profile.id);
      await loadList();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not update verification"), "error");
    } finally {
      setSavingCreds(false);
    }
  };

  const uploadLicence = async (file) => {
    const profile = detail?.profile;
    const clinicId = profile?.clinicId || activeClinicId;
    if (!clinicId || !profile?.id || !file) {
      if (!clinicId) {
        swal("Error", "Missing clinic for this practitioner. Refresh the list or contact support.", "error");
      }
      return;
    }
    setSavingProfile(true);
    setProfileFlash(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.post(
        `/api/v1/partners/medical/clinics/${clinicId}/practitioners/${profile.id}/licence-document`,
        fd,
        { suppressInlineErrorSwal: true }
      );
      setProfileFlash("Document uploaded (recorded as admin entry)");
      await loadDetail(profile.id);
      await loadList();
    } catch (e) {
      swal("Upload failed", formatAdminApiError(e, "Upload failed"), "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const postDecision = async (platformListingDecision) => {
    if (!activeId) return;

    if (platformListingDecision === "approved") {
      const row = rows.find((r) => String(r.id) === String(activeId));
      const miss = Array.isArray(row?.missingListFields) ? row.missingListFields : [];
      if (miss.length) {
        const labels = miss
          .map((m) => (m === "specialty" ? "specialty" : "TC price"))
          .join(" and ");
        const ok = await swal({
          title: "Approve but stay offline?",
          text: `This practitioner will be approved but stay offline until a specialty (and TC price) is set — they won't appear in the public catalog. Missing: ${labels}.`,
          icon: "warning",
          buttons: ["Cancel", "Approve anyway"],
          dangerMode: false,
        });
        if (!ok) return;
      }
    }

    setSavingDecision(true);
    try {
      const solo = detail?.isSoloOwner === true || detail?.profile?.isSoloOwner === true;
      const path = solo
        ? `/api/v1/admin/practitioner-approvals/${activeId}/listing-bundle`
        : `/api/v1/admin/practitioner-approvals/${activeId}/decision`;
      await http.patch(
        path,
        {
          platformListingDecision,
          rejectionReasons: platformListingDecision === "rejected" ? pickedReasons : [],
          rejectionNote: platformListingDecision === "rejected" ? note : "",
        },
        { suppressInlineErrorSwal: true }
      );
      setRejectOpen(false);
      await goNextAfterDecision(activeId);
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Decision failed"), "error");
    } finally {
      setSavingDecision(false);
    }
  };

  const remindListing = async () => {
    if (!activeId) return;
    setRemindingListing(true);
    try {
      await http.post(
        `/api/v1/admin/practitioner-approvals/${activeId}/remind-listing`,
        {},
        { suppressInlineErrorSwal: true }
      );
      swal("Sent", "Reminder email sent to the practitioner.", "success");
      await loadList();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not send reminder"), "error");
    } finally {
      setRemindingListing(false);
    }
  };

  const approveClinicListing = async () => {
    if (!activeId) return;
    const n = Number(detail?.clinicSiblingPendingCount) || 0;
    const siblingNote =
      n > 1
        ? ` This clinic has ${n} practitioners waiting — approving unblocks clinic listing for all of them.`
        : "";
    const ok = await swal({
      title: "Approve this clinic for public listing?",
      text: `Approving the practitioner alone does not make them visible — the clinic must be approved too.${siblingNote}`,
      icon: "warning",
      buttons: ["Cancel", "Approve clinic"],
      dangerMode: false,
    });
    if (!ok) return;
    setSavingClinicDecision(true);
    try {
      await http.patch(
        `/api/v1/admin/practitioner-approvals/${activeId}/clinic-decision`,
        { platformListingDecision: "approved" },
        { suppressInlineErrorSwal: true }
      );
      await loadDetail(activeId, activeClinicId);
      await loadList();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Clinic approval failed"), "error");
    } finally {
      setSavingClinicDecision(false);
    }
  };

  const rejectClinicListing = async () => {
    if (!activeId) return;
    const reason = await swal({
      title: "Reject clinic public listing?",
      text: "Practitioners on this clinic will stay off the public catalog.",
      content: {
        element: "input",
        attributes: {
          placeholder: "Optional note for the partner",
        },
      },
      buttons: ["Cancel", "Reject clinic"],
      dangerMode: true,
    });
    if (reason === null) return;
    setSavingClinicDecision(true);
    try {
      await http.patch(
        `/api/v1/admin/practitioner-approvals/${activeId}/clinic-decision`,
        {
          platformListingDecision: "rejected",
          rejectionReasons: ["other"],
          rejectionNote: typeof reason === "string" ? reason : "",
        },
        { suppressInlineErrorSwal: true }
      );
      await loadDetail(activeId, activeClinicId);
      await loadList();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Clinic rejection failed"), "error");
    } finally {
      setSavingClinicDecision(false);
    }
  };

  const approvePartnerProgram = async () => {
    const partnerId = detail?.profile?.partnerId;
    if (!partnerId) {
      swal("Error", "Missing partner for this practitioner.", "error");
      return;
    }
    setSavingClinicDecision(true);
    try {
      await http.put(
        `/api/v1/medical-partners/${partnerId}`,
        {
          tcApprovalStatus: "approved",
          tcApprovalUpdatedAt: new Date().toISOString(),
        },
        { suppressInlineErrorSwal: true }
      );
      await loadDetail(activeId, activeClinicId);
      await loadList();
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Partner program approval failed"), "error");
    } finally {
      setSavingClinicDecision(false);
    }
  };

  const toggleReason = (code) => {
    setPickedReasons((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const openDeletePanel = async () => {
    if (!activeId) return;
    setDeleteOpen(true);
    setDeleteLoading(true);
    setDeleteImpact(null);
    setDeleteChecks({});
    try {
      const res = await http.get(`/api/v1/admin/practitioner-approvals/${activeId}/deletion-impact`, {
        suppressInlineErrorSwal: true,
      });
      const impact = res.data?.data || null;
      setDeleteImpact(impact);
      const next = {};
      (impact?.items || []).forEach((item) => {
        if (item.included) next[item.id] = true;
      });
      setDeleteChecks(next);
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not load deletion preview"), "error");
      setDeleteOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleDeleteCheck = (id) => {
    setDeleteChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const requiredDeleteIds = useMemo(() => {
    if (!deleteImpact?.items) return [];
    return deleteImpact.items.filter((i) => i.included).map((i) => i.id);
  }, [deleteImpact]);

  const allDeleteChecked =
    requiredDeleteIds.length > 0
      ? requiredDeleteIds.every((id) => deleteChecks[id])
      : Boolean(deleteImpact?.canDelete);

  const confirmDelete = async () => {
    if (!activeId || !deleteImpact?.canDelete) return;
    if (!allDeleteChecked) {
      swal("Confirm", "Check every item that will be removed before confirming.", "warning");
      return;
    }
    const ok = await swal({
      title: "Delete this practitioner?",
      text: "This cannot be undone. Related empty records listed above will be removed permanently.",
      icon: "warning",
      buttons: ["Cancel", "Delete permanently"],
      dangerMode: true,
    });
    if (!ok) return;

    setDeleteSaving(true);
    try {
      await http.delete(`/api/v1/admin/practitioner-approvals/${activeId}`, {
        suppressInlineErrorSwal: true,
      });
      swal("Deleted", "Practitioner removed.", "success");
      await goNextAfterDecision(activeId);
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Deletion failed"), "error");
    } finally {
      setDeleteSaving(false);
    }
  };

  const profile = detail?.profile;
  const isSoloOwner = detail?.isSoloOwner === true || profile?.isSoloOwner === true;
  const licenceHref = assetUrl(profile?.licenceDocumentUrl);
  const options =
    detail?.rejectionReasonOptions ||
    Object.entries(REASON_LABELS).map(([code, label]) => ({ code, label }));
  const indicativeItems = (detail?.checklist?.indicative || []).filter((item) => !item.disabled);
  const clinicDecision = String(detail?.clinicListingDecision || "pending").toLowerCase();
  const clinicListingOk = clinicDecision === "approved";
  const practitionerDecision = String(detail?.decision || "pending").toLowerCase();
  const siblingCount = Number(detail?.clinicSiblingPendingCount) || 0;
  const partnerProgramStatus = String(profile?.partnerTcApprovalStatus || "").toLowerCase();
  const partnerProgramOk = partnerProgramStatus === "approved";
  const clinicBlocksApprove = !isSoloOwner && !clinicListingOk;
  const clinicScreenHref = profile?.partnerId
    ? getPublicUrl(`medical-partner-details/${profile.partnerId}`)
    : getPublicUrl("teleconsultations/pending-partners");

  return (
    <>
      <TeleconsultationPageShell
        headTitle="Catalogue go-live"
        title="Catalogue go-live"
        description="Review licence and clinic listing so practitioners appear in the public catalog. Invisible rows are approved practitioners blocked by a pending clinic."
        contentStyle={{
          marginRight: queueMarginRight,
          transition: "margin-right 0.2s ease",
        }}
        aside={
          <Button color="light" onClick={() => loadList()} disabled={loading}>
            <Icon name="refresh" />
            Refresh
          </Button>
        }
      >
        <div className="card card-bordered">
          <div className="card-inner">
            <div className="admin-golive-toolbar">
              <div className="admin-golive-toolbar__row">
                <div className="d-flex flex-column gap-2 mb-0">
                  <div className="custom-control custom-checkbox mb-0">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="hideTestAccounts"
                      checked={hideTest}
                      onChange={(e) => setHideTest(e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="hideTestAccounts">
                      Hide test accounts
                      {meta.hiddenTestCount > 0 ? (
                        <span className="text-soft"> ({meta.hiddenTestCount} hidden)</span>
                      ) : null}
                    </label>
                  </div>
                  {hideTest && meta.hiddenTestCount > 0 ? (
                    <Button
                      color="outline-primary"
                      size="sm"
                      type="button"
                      onClick={() => setHideTest(false)}
                    >
                      Show test accounts ({meta.hiddenTestCount})
                    </Button>
                  ) : null}
                  {!hideTest ? (
                    <Button color="light" size="sm" type="button" onClick={() => setHideTest(true)}>
                      Hide test accounts again
                    </Button>
                  ) : null}
                </div>
                <div className="admin-golive-toolbar__search">
                  <label className="form-label mb-1" htmlFor="pa-search">
                    Search
                  </label>
                  <input
                    id="pa-search"
                    type="search"
                    className="form-control"
                    placeholder="Name, email, clinic…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="admin-golive-toolbar__meta">
                  <span className="small text-soft">
                    {rows.length} in queue · {meta.invisibleCount || 0} invisible ·{" "}
                    {meta.notListableCount || 0} offline · {meta.licenceBlockedCount || 0} licence ·{" "}
                    {meta.clinicBlockedCount || 0} clinic · {meta.readyCount || 0} ready
                  </span>
                  {sortedRows.length > 0 ? (
                    <Button color="primary" size="sm" type="button" onClick={() => openRow(sortedRows[0])}>
                      Start review
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="admin-golive-filters" role="group" aria-label="Queue filters">
                {QUEUE_FILTERS.map((f) => (
                  <Button
                    key={f.id}
                    size="sm"
                    color={queueFilter === f.id ? "primary" : "light"}
                    outline={queueFilter !== f.id}
                    type="button"
                    onClick={() => setQueueFilter(f.id)}
                  >
                    {f.label}
                    {f.id === "invisible" && meta.invisibleCount
                      ? ` (${meta.invisibleCount})`
                      : ""}
                    {f.id === "notListable" && meta.notListableCount
                      ? ` (${meta.notListableCount})`
                      : ""}
                  </Button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center p-4 text-soft">
                <div className="spinner-border spinner-border-sm text-primary" role="status" /> Loading…
              </div>
            ) : sortedRows.length === 0 ? (
              <p className="text-soft mb-0">
                {queueFilter === "all"
                  ? "All caught up — no practitioners in this queue."
                  : "No practitioners match this filter."}
                {hideTest && meta.hiddenTestCount > 0 ? (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => setHideTest(false)}
                    >
                      Show {meta.hiddenTestCount} test account{meta.hiddenTestCount === 1 ? "" : "s"}
                    </button>
                  </>
                ) : null}
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-dark text-decoration-none fw-bold"
                          onClick={() => toggleSort(SORT_KEYS.name)}
                        >
                          Practitioner{sortHint(SORT_KEYS.name)}
                        </button>
                      </th>
                      <th className="d-none d-xl-table-cell">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-dark text-decoration-none fw-bold"
                          onClick={() => toggleSort(SORT_KEYS.country)}
                        >
                          Country{sortHint(SORT_KEYS.country)}
                        </button>
                      </th>
                      <th>Licence</th>
                      <th>Clinic</th>
                      <th>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-dark text-decoration-none fw-bold"
                          onClick={() => toggleSort(SORT_KEYS.status)}
                        >
                          Visibility{sortHint(SORT_KEYS.status)}
                        </button>
                      </th>
                      <th className="d-none d-xl-table-cell">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-dark text-decoration-none fw-bold"
                          onClick={() => toggleSort(SORT_KEYS.requestedAt)}
                        >
                          Requested{sortHint(SORT_KEYS.requestedAt)}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => {
                      const vis = visibilityLabel(row);
                      const lic = licenceCell(row);
                      const clinicOk = !row.clinicBlocked;
                      return (
                        <tr
                          key={row.id}
                          role="button"
                          tabIndex={0}
                          className={[
                            String(activeId) === String(row.id) ? "table-active" : "",
                            row.invisible ? "table-danger" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => openRow(row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openRow(row);
                            }
                          }}
                        >
                          <td>
                            <strong>{row.name}</strong>
                            {row.isTest ? (
                              <span className="badge bg-secondary ms-1">Test</span>
                            ) : null}
                            {row.isSoloOwner ? (
                              <span className="badge bg-info ms-1">Solo</span>
                            ) : null}
                            <span className="d-block small text-soft text-break d-xl-none">
                              {row.email || "—"}
                            </span>
                          </td>
                          <td className="d-none d-xl-table-cell">{row.practiceCountry || "—"}</td>
                          <td>
                            <span className={`badge ${lic.ok ? "bg-success" : "bg-danger"}`}>
                              {lic.ok ? "OK" : "Block"} · {lic.label}
                            </span>
                          </td>
                          <td className="small">
                            <span className={`badge ${clinicOk ? "bg-success" : "bg-danger"}`}>
                              {clinicOk ? "OK" : "Block"} ·{" "}
                              {clinicDecisionLabel(row.clinicListingDecision)}
                            </span>
                            <span className="d-block text-soft">{row.clinicName || "—"}</span>
                          </td>
                          <td>
                            <span className={vis.className}>{vis.text}</span>
                            {vis.hint ? (
                              <span className="d-block small text-soft">{vis.hint}</span>
                            ) : null}
                          </td>
                          <td className="small text-nowrap d-none d-xl-table-cell">
                            {formatDate(row.requestedAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </TeleconsultationPageShell>

      {activeId ? (
        <aside
          className="bg-white border-start shadow-sm d-flex flex-column"
          style={
            isNarrowPanel
              ? {
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: "100%",
                  height: "100vh",
                  zIndex: 1040,
                }
              : {
                  position: "fixed",
                  top: 65,
                  right: 0,
                  width: PANEL_WIDTH,
                  height: "calc(100vh - 65px)",
                  zIndex: 1020,
                }
          }
        >
          <div className="border-bottom p-3 d-flex align-items-start justify-content-between">
            <div className="min-w-0">
              <h6 className="title mb-1 text-truncate">{profile?.name || "Practitioner"}</h6>
              <p className="small text-soft mb-0 text-break">{profile?.email || "—"}</p>
              <p className="small text-soft mb-0 text-break">{profile?.phone || "—"}</p>
              <p className="small text-soft mb-0 text-truncate">
                {profile?.clinicName || "—"}
                {profile?.partnerName ? ` · ${profile.partnerName}` : ""}
                {profile?.practiceCountry ? ` · ${profile.practiceCountry}` : ""}
              </p>
                  {activeIndex >= 0 ? (
                    <p className="small text-soft mb-0 mt-1">
                      {activeIndex + 1} / {sortedRows.length}
                      {autoAdvance ? " · next in queue" : ""}
                    </p>
                  ) : null}
            </div>
            <Button color="light" size="sm" className="flex-shrink-0" onClick={closeDrawer}>
              Close
            </Button>
          </div>

          <div className="flex-grow-1 overflow-auto p-3">
            {detailLoading || !detail ? (
              <div className="text-center text-soft py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status" /> Loading…
              </div>
            ) : (
              <>
                <h6 className="overline-title text-soft mb-2">Visibility</h6>
                <div
                  className={`border rounded p-2 mb-3 ${
                    clinicListingOk && practitionerDecision === "approved"
                      ? "border-success"
                      : "border-warning"
                  }`}
                  role="status"
                >
                  {isSoloOwner ? (
                    <>
                      <p className="small mb-1">
                        <strong>Solo owner</strong> — clinic and practitioner listing are approved
                        together.
                      </p>
                      <p className="small mb-0">
                        Catalog listing:{" "}
                        <span
                          className={
                            clinicListingOk && practitionerDecision === "approved"
                              ? "text-success"
                              : "text-warning"
                          }
                        >
                          {clinicDecisionLabel(
                            clinicListingOk && practitionerDecision === "approved"
                              ? "approved"
                              : practitionerDecision === "rejected" || clinicDecision === "rejected"
                                ? "rejected"
                                : "pending"
                          )}
                        </span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="small mb-1">
                        <strong>Practitioner listing:</strong>{" "}
                        {clinicDecisionLabel(practitionerDecision)}
                        {" · "}
                        <strong>Clinic listing:</strong> {clinicDecisionLabel(clinicDecision)}
                      </p>
                      {!clinicListingOk ? (
                        <p className="small text-danger mb-0">
                          Invisible on catalog while the clinic is not approved — even if the
                          practitioner is already approved.
                        </p>
                      ) : practitionerDecision === "approved" ? (
                        <p className="small text-success mb-0">
                          Visible on catalog when other bookable rules pass.
                        </p>
                      ) : (
                        <p className="small text-soft mb-0">
                          Clinic is approved. Complete licence checks, then approve the practitioner.
                        </p>
                      )}
                    </>
                  )}
                </div>

                <h6 className="overline-title text-soft mb-2">Step 1 — Partner program (TC)</h6>
                <div className="border rounded p-2 mb-3">
                  <p className="small mb-1">
                    Partner workflow:{" "}
                    <span className="fw-bold">{profile?.partnerStatus || "—"}</span>
                    {" · "}
                    Program TC:{" "}
                    <span className={partnerProgramOk ? "text-success" : "text-warning"}>
                      {partnerProgramStatus || "—"}
                    </span>
                  </p>
                  <p className="small text-soft mb-2">
                    Distinct from public catalog listing. Approve here if the partner program is still pending.
                  </p>
                  {!partnerProgramOk && profile?.partnerId ? (
                    <Button
                      color="primary"
                      size="sm"
                      outline
                      disabled={savingClinicDecision}
                      onClick={approvePartnerProgram}
                    >
                      Approve partner program
                    </Button>
                  ) : null}
                </div>

                <h6 className="overline-title text-soft mb-2">Profile photo</h6>
                <div className="border rounded p-2 mb-3">
                  <ProfilePhotoBlock
                    clinicId={profile?.clinicId || activeClinicId}
                    practitionerId={profile?.id}
                    photoUrl={profilePhotoUrl}
                    displayName={profile?.name}
                    onChanged={(url) => setProfilePhotoUrl(url || "")}
                  />
                </div>

                <h6 className="overline-title text-soft mb-2">Step 2a — Licence</h6>
                <LicenceWorkflowHelp />

                <h6 className="overline-title text-soft mb-2">Documents provided by the practitioner</h6>
                <div className="border rounded p-2 mb-3">
                  <p className="small text-soft mb-0">
                    Licence document supplied by the practitioner (or upload below if you received it by email).
                  </p>
                  {licenceHref ? (
                    <div className="mb-2 mt-2">
                      <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                        <span className="small fw-bold">Licence document</span>
                        <a
                          className="btn btn-sm btn-outline-primary"
                          href={licenceHref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open document
                        </a>
                      </div>
                      {/\.(png|jpe?g|webp|gif)(\?|$)/i.test(licenceHref) ? (
                        <img
                          src={licenceHref}
                          alt="Licence document"
                          className="img-fluid border rounded mt-2 d-block"
                          style={{ maxHeight: 160 }}
                        />
                      ) : (
                        <iframe
                          title="Licence document"
                          src={licenceHref}
                          className="w-100 border rounded mt-2"
                          style={{ height: 160 }}
                        />
                      )}
                    </div>
                  ) : (
                    <p className="small text-soft mb-2 mt-2">No document uploaded.</p>
                  )}
                  <label
                    className={`btn btn-light btn-sm mb-0 ${savingProfile ? "disabled" : ""}`}
                    style={{ cursor: savingProfile ? "not-allowed" : "pointer" }}
                  >
                    <input
                      type="file"
                      className="d-none"
                      disabled={savingProfile}
                      accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        e.target.value = "";
                        if (f) uploadLicence(f);
                      }}
                    />
                    Upload document
                  </label>
                  {profile?.licenceAdminEntryAt ? (
                    <p className="small text-soft mb-0 mt-2">
                      Licence details entered by admin on {formatDate(profile.licenceAdminEntryAt)}
                    </p>
                  ) : null}
                  {profileFlash && !savingProfile ? (
                    <p className="small text-success mb-0 mt-1" role="status">
                      {profileFlash}
                    </p>
                  ) : null}
                  {savingProfile ? (
                    <p className="small text-primary mb-0 mt-1">Saving…</p>
                  ) : null}
                </div>

                <h6 className="overline-title text-soft mb-2">Licence details</h6>
                <div className="mb-3">
                  <label className="form-label small mb-1" htmlFor="pa-licence-num">
                    {registrationNumberLabel(profile?.practiceCountry)}
                  </label>
                  <input
                    id="pa-licence-num"
                    type="text"
                    className="form-control form-control-sm"
                    defaultValue={profile?.medicalLicenceNumber || profile?.professionalId || ""}
                    placeholder="Official registration number"
                    disabled={savingProfile}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      const prev = (profile?.medicalLicenceNumber || profile?.professionalId || "").trim();
                      if (v === prev) return;
                      saveProfile({ medicalLicenceNumber: v || null }, "Registration number saved");
                    }}
                  />
                  {registryForCountry(profile?.practiceCountry) ? (
                    <p className="small mb-2 mt-1">
                      <a
                        href={registryForCountry(profile?.practiceCountry).url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {registryForCountry(profile?.practiceCountry).label}
                      </a>
                    </p>
                  ) : null}
                  <label className="form-label small mb-1 mt-2" htmlFor="pa-country">
                    Country of practice
                  </label>
                  <input
                    id="pa-country"
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 80 }}
                    defaultValue={profile?.practiceCountry || ""}
                    placeholder="FR"
                    maxLength={2}
                    disabled={savingProfile}
                    onBlur={(e) => {
                      const v = e.target.value.trim().toUpperCase();
                      const prev = (profile?.practiceCountry || "").trim().toUpperCase();
                      if (v === prev) return;
                      if (v && !/^[A-Z]{2}$/.test(v)) {
                        swal("Validation", "Country must be a 2-letter ISO code (e.g. FR).", "warning");
                        e.target.value = profile?.practiceCountry || "";
                        return;
                      }
                      saveProfile({ practiceCountry: v || null }, "Country saved");
                    }}
                  />
                </div>

                <div className="border rounded p-2 mb-3">
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="credsVerifiedToggle"
                      checked={!!profile?.credentialsVerified}
                      disabled={savingCreds}
                      onChange={(e) => toggleCredentials(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="credsVerifiedToggle">
                      I have verified this licence
                    </label>
                  </div>
                  <p className="small text-soft mb-0">
                    Check after confirming the number with the official registry for this country. This
                    verification is your attestation as an agent — separate from approving public listing.
                  </p>
                  {profile?.credentialsVerified && profile?.credentialsVerifiedAt ? (
                    <p className="small text-success mb-0 mt-2">
                      {formatVerifierLine(profile.credentialsVerifiedByName, profile.credentialsVerifiedAt)}
                    </p>
                  ) : null}
                  {savingCreds ? (
                    <p className="small text-primary mb-0 mt-1">Saving…</p>
                  ) : null}
                  {credsFlash && !savingCreds ? (
                    <p className="small text-success mb-0 mt-1" role="status">
                      {credsFlash}
                    </p>
                  ) : null}
                </div>

                {detail && !isSoloOwner ? (
                    <div
                      className={`border rounded p-2 mb-3 ${
                        clinicListingOk ? "border-success" : "border-warning"
                      }`}
                      role="status"
                    >
                      <div className="small fw-bold mb-0">Step 2b — Clinic public listing</div>
                      <p className="small text-soft mb-0">
                        {profile?.clinicName || "Clinic"} ·{" "}
                        <span className={clinicListingOk ? "text-success" : "text-warning"}>
                          {clinicDecisionLabel(clinicDecision)}
                        </span>
                      </p>
                      {siblingCount > 0 ? (
                        <p className="small mb-2 mt-2">
                          <strong>
                            {siblingCount} practitioner{siblingCount === 1 ? "" : "s"} waiting on
                            this clinic
                          </strong>
                          {siblingCount > 1 ? (
                            <span className="text-soft">
                              {" "}
                              — approving the clinic unblocks listing for all of them (each still
                              needs an approved practitioner listing).
                            </span>
                          ) : null}
                        </p>
                      ) : null}
                      {!clinicListingOk ? (
                        <>
                          <p className="small text-danger mb-2">
                            Approving this practitioner will not make them visible while the clinic
                            is not approved.
                          </p>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              color="warning"
                              size="sm"
                              disabled={
                                savingClinicDecision ||
                                savingDecision ||
                                clinicDecision === "rejected"
                              }
                              onClick={approveClinicListing}
                            >
                              {savingClinicDecision ? "Saving…" : "Approve clinic"}
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              disabled={savingClinicDecision || savingDecision}
                              onClick={rejectClinicListing}
                            >
                              Reject clinic
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              outline
                              onClick={() => history.push(clinicScreenHref)}
                            >
                              Open partner dossier
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}

                {isSoloOwner ? (
                  <div className="border rounded p-2 mb-3 border-info" role="status">
                    <div className="small fw-bold mb-0">Solo practice</div>
                    <p className="small text-soft mb-0">
                      Approving for catalog updates clinic and practitioner listing in one step.
                      No separate clinic approval.
                    </p>
                  </div>
                ) : null}

                <h6 className="overline-title text-soft mb-2">Checklist</h6>
                <ChecklistBlock
                  title="Required to approve"
                  items={detail.checklist?.blocking}
                />
                {indicativeItems.length > 0 ? (
                  <details className="mb-3">
                    <summary className="small text-soft fw-bold" style={{ cursor: "pointer" }}>
                      Optional checks ({indicativeItems.filter((i) => !i.ok).length} open)
                    </summary>
                    <ul className="list-unstyled mb-0 small mt-2">
                      {indicativeItems.map((item) => (
                        <li key={item.id} className="d-flex align-items-start gap-2 mb-1">
                          <span aria-hidden="true">{item.ok ? "✅" : "○"}</span>
                          <span className={item.ok ? "text-soft" : ""}>{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}

                <h6 className="overline-title text-soft mb-2">
                  {isSoloOwner
                    ? "Step 2b — Public catalog listing"
                    : "Step 2c — Practitioner public listing"}
                </h6>
                {(() => {
                  const listRow = rows.find((r) => String(r.id) === String(activeId));
                  if (!listRow?.notListable) return null;
                  const miss = (listRow.missingListFields || [])
                    .map((m) => (m === "specialty" ? "specialty" : "video consultation price"))
                    .join(", ");
                  return (
                    <div className="alert alert-warning py-2 small mb-2" role="status">
                      <strong>Approved but not online</strong>
                      {miss ? ` — missing: ${miss}.` : "."} Patients cannot find this practitioner
                      until these are completed.
                      <div className="mt-2">
                        <Button
                          color="warning"
                          size="sm"
                          disabled={remindingListing}
                          onClick={remindListing}
                        >
                          <Icon name="mail" className="me-1" />
                          {remindingListing ? "Sending…" : "Remind"}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Button
                    color="success"
                    size="sm"
                    disabled={savingDecision || !detail.canApprove}
                    onClick={() => postDecision("approved")}
                  >
                    {isSoloOwner ? "Approve for catalog" : "Approve practitioner"}
                  </Button>
                  {!rejectOpen && meCan("directory.practitioner_reject.act") ? (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      disabled={savingDecision || deleteSaving}
                      onClick={() => setRejectOpen(true)}
                    >
                      Reject
                    </Button>
                  ) : null}
                  {meCan("directory.practitioner_delete.act") ? (
                    <Button
                      color="danger"
                      size="sm"
                      disabled={savingDecision || deleteSaving || deleteLoading}
                      onClick={openDeletePanel}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
                {clinicBlocksApprove && !detail.canApprove ? (
                  <p className="small text-danger mb-2" role="status">
                    Approve the clinic first — this practitioner stays invisible until the clinic
                    is approved.
                  </p>
                ) : !detail.canApprove ? (
                  <p className="small text-soft mb-2" role="status">
                    Complete the required checklist items before approving.
                  </p>
                ) : null}
                {deleteOpen ? (
                  <div className="border border-danger rounded p-2 mb-3">
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                      <div className="text-soft small fw-bold">Delete — related data</div>
                      <Button
                        color="light"
                        size="sm"
                        disabled={deleteSaving}
                        onClick={() => {
                          setDeleteOpen(false);
                          setDeleteImpact(null);
                        }}
                      >
                        Close
                      </Button>
                    </div>
                    {deleteLoading || !deleteImpact ? (
                      <p className="small text-soft mb-0">Loading impact…</p>
                    ) : (
                      <>
                        {deleteImpact.blocked ? (
                          <div className="alert alert-warning py-2 px-2 small mb-2" role="alert">
                            {deleteImpact.blocked.message}
                          </div>
                        ) : null}
                        {deleteImpact.isSelf ? (
                          <div className="alert alert-danger py-2 px-2 small mb-2" role="alert">
                            You cannot delete your own account.
                          </div>
                        ) : null}
                        <ul className="list-unstyled mb-2 small">
                          {(deleteImpact.items || []).map((item) => {
                            const label =
                              item.id === "consultations" && item.upcoming != null
                                ? `${item.label} (${item.count}) — upcoming (${item.upcoming})`
                                : `${item.label} (${item.count})`;
                            const showCheck = item.included;
                            return (
                              <li key={item.id} className="mb-1">
                                {showCheck ? (
                                  <label className="form-check mb-0 d-flex align-items-start gap-2">
                                    <input
                                      type="checkbox"
                                      className="form-check-input mt-1"
                                      checked={!!deleteChecks[item.id]}
                                      disabled={deleteSaving || !deleteImpact.canDelete}
                                      onChange={() => toggleDeleteCheck(item.id)}
                                    />
                                    <span>
                                      {label}
                                      {item.note ? (
                                        <span className="d-block text-soft">{item.note}</span>
                                      ) : null}
                                    </span>
                                  </label>
                                ) : (
                                  <span className={item.blocking ? "text-danger" : "text-soft"}>
                                    {label}
                                    {item.blocking ? " — blocks deletion" : ""}
                                    {item.note ? (
                                      <span className="d-block text-soft">{item.note}</span>
                                    ) : null}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                        {deleteImpact.canDelete ? (
                          <>
                            <p className="small mb-2">
                              Summary: membership will be removed
                              {deleteImpact.willDelete?.soloClinic ? ", solo clinic deleted" : ""}
                              {deleteImpact.willDelete?.userAccount ? ", user account deleted" : ""}
                              {deleteImpact.willDelete?.partnerIfOrphan
                                ? ", orphan individual partner deleted"
                                : ""}
                              .
                            </p>
                            <Button
                              color="danger"
                              size="sm"
                              disabled={deleteSaving || !allDeleteChecked}
                              onClick={confirmDelete}
                            >
                              {deleteSaving ? "Deleting…" : "Confirm deletion"}
                            </Button>
                          </>
                        ) : (
                          <p className="small text-soft mb-0">
                            Deletion is not available for this record. Clear medical data manually
                            first, or contact engineering for a guided cleanup.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ) : null}
                {rejectOpen ? (
                  <div className="border rounded p-2 mb-2">
                    <div className="text-soft small fw-bold mb-1">Rejection reasons</div>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {options.map((opt) => (
                        <label key={opt.code} className="form-check form-check-sm mb-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={pickedReasons.includes(opt.code)}
                            onChange={() => toggleReason(opt.code)}
                          />
                          <span className="form-check-label small">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <textarea
                      className="form-control form-control-sm mb-2"
                      rows={2}
                      placeholder="Optional note for the partner"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        color="danger"
                        size="sm"
                        disabled={savingDecision}
                        onClick={() => postDecision("rejected")}
                      >
                        Confirm reject
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        disabled={savingDecision}
                        onClick={() => setRejectOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
                {!detail.canApprove ? (
                  <p className="text-soft small mt-1 mb-0">
                    Approve is disabled until required checks pass. Complete licence details and verify
                    above if needed.
                  </p>
                ) : null}
              </>
            )}
          </div>
        </aside>
      ) : null}
    </>
  );
};

export default PractitionerApprovals;
